import React from "react";
import {
  Box,
  Pagination,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import dayjs from "dayjs";
import { useGetEmployeeRedeemStatusesQuery } from "../../../../../redux-toolkit/features/rewards/rewardsApiSlice";
import ProgressBar from "../../../../UI/ProgressBar";
import "../../../../UI/Tables/TableUi.css";
import "../Employee.css";
import "../../../../Admin/Task Verification/Task Detailed/TaskDetailed.css";
import { EmployeeActivityEmpty } from "./EmployeeActivityEmpty";
import "./EmployeeActivityPage.css";

const sectionTabSx = {
  textTransform: "capitalize",
  fontWeight: "500",
  fontSize: "15px",
};

const PAGE_SIZE = 10;

export const EmployeeActivityRewards = ({ employeeId }) => {
  const [openSection, setOpenSection] = React.useState("PENDING");
  const [sectionPages, setSectionPages] = React.useState({
    PENDING: 1,
    COMPLETED: 1,
  });

  const { data, isLoading, isFetching } = useGetEmployeeRedeemStatusesQuery(
    {
      userId: employeeId,
      status: openSection,
      page: sectionPages[openSection] || 1,
      limit: PAGE_SIZE,
      sortBy: "date",
      sortType: "desc",
    },
    { refetchOnMountOrArgChange: true, skip: !employeeId }
  );

  const orders = data?.data?.orders || [];
  const counts = data?.data?.counts || { pending: 0, completed: 0, rejected: 0 };
  const pagination = data?.data?.pagination || {
    totalPages: 1,
    totalResults: 0,
  };

  const sectionConfig = [
    { key: "PENDING", label: "Pending", count: counts.pending || 0 },
    { key: "COMPLETED", label: "Delivered", count: counts.completed || 0 },
  ];

  const activeTabIndex = Math.max(
    0,
    sectionConfig.findIndex((section) => section.key === openSection)
  );

  return (
    <div className="employeeActivityPage__section">
      <Box className="employeeActivityPage__statusTabs" sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTabIndex}
            onChange={(_event, newValue) =>
              setOpenSection(sectionConfig[newValue]?.key || "PENDING")
            }
            aria-label="employee reward status tabs"
          >
            {sectionConfig.map((section) => (
              <Tab
                key={section.key}
                label={`${section.label} (${section.count})`}
                sx={sectionTabSx}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {isLoading || isFetching ? (
        <div className="employeeActivityPage__loader">
          <ProgressBar />
        </div>
      ) : (
        <div className="employeeActivityPage__list">
          {orders.length > 0 ? (
            <TableContainer sx={{ maxWidth: "100%" }}>
              <Table stickyHeader aria-label="employee rewards table">
                <TableHead>
                  <TableRow className="tableUi__head">
                    <TableCell align="center">Product name</TableCell>
                    <TableCell align="center">Product points</TableCell>
                    <TableCell align="center">Product quantity</TableCell>
                    <TableCell align="center">Requested date</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <React.Fragment key={order?.items?._id || order?._id}>
                      <div className="row__spacing"></div>
                      <TableRow className="tableUi___body">
                        <TableCell align="center">
                          {order?.items?.product?.name || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <StarBorderOutlinedIcon sx={{ fontSize: 16 }} />
                            {order?.items?.points ??
                              order?.items?.product?.points ??
                              0}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {order?.items?.quantity || 0}
                        </TableCell>
                        <TableCell align="center">
                          {order?.createdAt
                            ? dayjs(order.createdAt).format("DD/MM/YY")
                            : "--"}
                        </TableCell>
                        <TableCell align="center">
                          {order?.items?.status === "COMPLETED"
                            ? "Delivered"
                            : order?.items?.status || openSection}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmployeeActivityEmpty
              title={
                openSection === "COMPLETED"
                  ? "No Delivered Rewards Found!"
                  : "No Pending Rewards Found!"
              }
            />
          )}
        </div>
      )}

      <div className="employeeActivityPage__pagination">
        <Pagination
          count={Math.max(1, pagination.totalPages || 1)}
          shape="rounded"
          page={sectionPages[openSection] || 1}
          onChange={(_, newPage) =>
            setSectionPages((prev) => ({
              ...prev,
              [openSection]: newPage,
            }))
          }
        />
      </div>
    </div>
  );
};
